// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import React, { useCallback, useRef } from 'react';
import { getFeedbackProvider, IFeedbackLaunchParams } from './feedback-provider';
import { useTheme } from '../../data/theme-context';
import { trackError } from '../../utilities';
import { useConfig } from '../../data/config-context';
import useAsyncEffect from 'use-async-effect';

interface InAppFeedbackContextValue {
    launch: () => void;
}

const InAppFeedbackContext = React.createContext<InAppFeedbackContextValue>({ launch: () => {} });

interface InAppFeedbackProviderProps {
    children: React.ReactNode;
}

const InAppFeedbackProvider: React.FC<InAppFeedbackProviderProps> = ({ children }) => {
    const feedbackDivRef = useRef<HTMLDivElement>(document.createElement('div'));
    const feedbackProvider = getFeedbackProvider(feedbackDivRef.current);
    const { ocvFeedback, feedbackUrl, ocvFeedbackAppId, ocvFeedbackHostName, useOcvProdEndpoint } = useConfig();
    const [isInitialized, setIsInitialized] = React.useState(false);
    const [theme] = useTheme();

    const launch = useCallback(async () => {
        if (!ocvFeedback) {
            window.open(feedbackUrl, '_blank');
            return;
        } else if (!isInitialized) {
            // TODO: Replace with web version of ocv feedback
            window.open(feedbackUrl, '_blank');
            return;
        }

        const launchParams: IFeedbackLaunchParams = { theme: theme.profileTheme };
        try {
            await feedbackProvider.launch(launchParams);
        } catch (e) {
            trackError(e);
            // TODO: Replace with web version of ocv feedback
            window.open(feedbackUrl, '_blank');
        }
    }, [feedbackProvider, feedbackUrl, isInitialized, ocvFeedback, theme.profileTheme]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useAsyncEffect(async () => {
        await feedbackProvider.initialize({
            appId: Number(ocvFeedbackAppId),
            hostName: ocvFeedbackHostName,
            isProd: useOcvProdEndpoint === 'true',
            locale: 'en',
        });
        setIsInitialized(true);
    }, [feedbackProvider, ocvFeedbackAppId, ocvFeedbackHostName, useOcvProdEndpoint]);

    return (
        <InAppFeedbackContext.Provider value={{ launch }}>
            {children}
            <div ref={feedbackDivRef} style={{ display: 'none' }} />
        </InAppFeedbackContext.Provider>
    );
};

export { InAppFeedbackProvider, InAppFeedbackContext };
